import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Header from '../../layouts/Header';
import { Container, Spinner, Form } from 'react-bootstrap';
import axiosInstance from '../../api/axios';
import OutletSelect from '../../components/OutletSelect'; // adjust path as needed
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
// import Images from '../../assets/images';
import IntendImages from '../../assets/img/indentImg.png';

/* ─── constants ──────────────────────────────────────────── */
const STATUS_CONFIG = {
    PENDING: { label: 'Pending', color: '#d97706', bg: '#fef3c7', border: '#fcd34d' },
    ACCEPTED: { label: 'Accepted', color: '#059669', bg: '#d1fae5', border: '#6ee7b7' },
    REJECTED: { label: 'Rejected', color: '#dc2626', bg: '#fee2e2', border: '#fca5a5' },
    COMPLETED: { label: 'Completed', color: '#7c3aed', bg: '#ede9fe', border: '#c4b5fd' },
    // FULFILLED: { label: 'Fulfilled', color: '#7c3aed', bg: '#ede9fe', border: '#c4b5fd' },
    RECEIVED: { label: 'Received', color: '#2563eb', bg: '#dbeafe', border: '#93c5fd' },
};

// API only allows setting these two statuses
const UPDATABLE_STATUSES = {
    ACCEPTED: { label: 'Accepted', color: '#059669', bg: '#d1fae5', border: '#6ee7b7' },
    REJECTED: { label: 'Rejected', color: '#dc2626', bg: '#fee2e2', border: '#fca5a5' },
};

const fmt = (iso) =>
    new Date(iso).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });

const formatDate = (iso) =>
    new Date(iso).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });

/* ─── PDF Download Function ──────────────────────────────── */
const handleDownloadPDF = (request) => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const primaryColor = [9, 165, 247];
    const upperStatus = (request.status || 'PENDING').toUpperCase();

    // ── Header background ──
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, 28, 'F');

    // Logo
    try {
        doc.addImage(IntendImages, 'PNG', 10, 4, 50, 20);
    } catch (e) {}

    // Header text
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text('STOCK REQUEST', pageWidth - 14, 13, { align: 'right' });
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`${request.indentNumber || ''}`, pageWidth - 14, 20, { align: 'right' });

    doc.setTextColor(40, 40, 40);

    /* -------- BASIC INFO -------- */
    const startY = 36;
    const col1Label = 14;
    const col1Value = 38;
    const col2Label = 110;
    const col2Value = 128;

    doc.setFontSize(8.5);

    // ── Left side: Indent details ──
    doc.setFont('helvetica', 'normal');
    doc.text('Indent :', col1Label, startY);
    doc.setFont('helvetica', 'bold');
    doc.text(request.indentNumber || '-', col1Value, startY);

    doc.setFont('helvetica', 'normal');
    doc.text('Status :', col1Label, startY + 6);
    doc.setFont('helvetica', 'bold');
    doc.text(upperStatus, col1Value, startY + 6);

    doc.setFont('helvetica', 'normal');
    doc.text('Date :', col1Label, startY + 12);
    doc.setFont('helvetica', 'bold');
    doc.text(formatDate(request.createdAt), col1Value, startY + 12);

    doc.setFont('helvetica', 'normal');
    doc.text('Fin. Year :', col1Label, startY + 18);
    doc.setFont('helvetica', 'bold');
    doc.text(request.fin_year || '-', col1Value, startY + 18);

    // ── Right side: Outlet details ──
    doc.setFont('helvetica', 'normal');
    doc.text('Outlet :', col2Label, startY);
    doc.setFont('helvetica', 'bold');
    doc.text(request?.outlet?.name || '-', col2Value, startY);

    doc.setFont('helvetica', 'normal');
    doc.text('City :', col2Label, startY + 6);
    doc.setFont('helvetica', 'bold');
    doc.text(request?.outlet?.city || '-', col2Value, startY + 6);

    doc.setFont('helvetica', 'normal');
    doc.text('Address :', col2Label, startY + 12);
    doc.setFont('helvetica', 'bold');
    const address = request?.outlet?.address || '-';
    const addressLines = doc.splitTextToSize(address, 60);
    doc.text(addressLines, col2Value, startY + 12);

    // ── Divider line ──
    const dividerY = startY + 28;
    doc.setDrawColor(200, 200, 200);
    doc.line(14, dividerY, pageWidth - 14, dividerY);

    /* -------- TABLE -------- */
    const items = request.items || [];
    let totalRequested = 0;
    const showReceivedQty = request.status === 'COMPLETED' || request.status === 'RECEIVED';

    const sortedItems = [...items].sort((a, b) => {
        const mainA = a.rawMaterial?.mainCategory?.name || '';
        const mainB = b.rawMaterial?.mainCategory?.name || '';
        const mainCompare = mainA.localeCompare(mainB);
        if (mainCompare !== 0) return mainCompare;
        const subA = a.rawMaterial?.subCategory?.name || '';
        const subB = b.rawMaterial?.subCategory?.name || '';
        return subA.localeCompare(subB);
    });

    const tableRows = sortedItems.map((it, index) => {
        const rm = it.rawMaterial || {};
        const requested = Number(it.requestedQuantity || 0);
        const received = Number(it.receivedQuantity || 0);
        totalRequested += requested;

        return [
            index + 1,
            rm.name || '-',
            rm.mainCategory?.name || '-',
            rm.subCategory?.name || '-',
            rm.unit || '-',
            requested.toFixed(2),
            showReceivedQty ? received.toFixed(2) : '-',
        ];
    });

    autoTable(doc, {
        startY: dividerY + 6,
        head: [['#', 'Material', 'Main Cat', 'Sub Cat', 'Unit', 'Req Qty', 'Rec Qty']],
        body: tableRows,
        theme: 'grid',
        headStyles: {
            fillColor: primaryColor,
            textColor: 255,
            fontStyle: 'bold',
            halign: 'center',
            fontSize: 8,
        },
        styles: {
            fontSize: 8,
            cellPadding: 2.5,
            font: 'helvetica',
            valign: 'middle',
        },
        alternateRowStyles: {
            fillColor: [245, 250, 255],
        },
        columnStyles: {
            0: { halign: 'center', cellWidth: 10 },
            1: { halign: 'left' },
            2: { halign: 'center', cellWidth: 28 },
            3: { halign: 'center', cellWidth: 28 },
            4: { halign: 'center', cellWidth: 16 },
            5: { halign: 'center', cellWidth: 18 },
            6: { halign: 'center', cellWidth: 18 },
        },
    });

    /* -------- TOTALS -------- */
    const finalY = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.text(
        `Total Items: ${items.length}   |   Total Requested: ${totalRequested.toFixed(2)}`,
        pageWidth - 14,
        finalY,
        { align: 'right' }
    );

    /* -------- SIGNATURE -------- */
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.text('Authorized Signature', pageWidth - 60, finalY + 18);
    doc.line(pageWidth - 60, finalY + 20, pageWidth - 14, finalY + 20);

    /* -------- FOOTER -------- */
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.text(
        `Generated on ${new Date().toLocaleString('en-IN')}`,
        14,
        doc.internal.pageSize.getHeight() - 10
    );

    doc.save(`Stock-Request-${request.indentNumber || 'Details'}.pdf`);
};

/* ─── Combined PDF (PENDING + ACCEPTED aggregated) ──────── */
// const handleDownloadCombinedPDF = (allRequests) => {
//     const doc = new jsPDF('p', 'mm', 'a4');
//     const pageWidth = doc.internal.pageSize.getWidth();
//     const primaryColor = [9, 165, 247];

//     // ── Header background ──
//     doc.setFillColor(...primaryColor);
//     doc.rect(0, 0, pageWidth, 24, 'F');

//     // Logo
//     try { doc.addImage(IntendImages, 'PNG', 10, 0, 50, 20); } catch (e) { }

//     // Header text
//     doc.setTextColor(255, 255, 255);
//     doc.setFont('helvetica', 'bold');
//     doc.setFontSize(16);
//     doc.text('COMBINED STOCK REQUEST', pageWidth - 14, 10, { align: 'right' });
//     doc.setFontSize(9);
//     doc.setFont('helvetica', 'normal');
//     doc.text('Pending + Accepted Requisitions', pageWidth - 14, 16, { align: 'right' });

//     doc.setTextColor(40, 40, 40);

//     // ── Basic info ──
//     let startY = 34;
//     doc.setFontSize(9);
//     doc.setFont('helvetica', 'normal');
//     doc.text('Report Type:', 14, startY);
//     doc.setFont('helvetica', 'bold');
//     doc.text('Pending + Accepted', 44, startY);

//     doc.setFont('helvetica', 'normal');
//     doc.text('Date:', 110, startY);
//     doc.setFont('helvetica', 'bold');
//     doc.text(new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }), 130, startY);

//     // Count per status
//     const pendingReqs = allRequests.filter(r => r.status === 'PENDING');
//     const acceptedReqs = allRequests.filter(r => r.status === 'ACCEPTED');

//     doc.setFont('helvetica', 'normal');
//     doc.text('Pending Requests:', 14, startY + 6);
//     doc.setFont('helvetica', 'bold');
//     doc.text(String(pendingReqs.length), 52, startY + 6);

//     doc.setFont('helvetica', 'normal');
//     doc.text('Accepted Requests:', 14, startY + 12);
//     doc.setFont('helvetica', 'bold');
//     doc.text(String(acceptedReqs.length), 52, startY + 12);

//     // ── Aggregate materials across PENDING + ACCEPTED ──
//     // Key: rawMaterial._id → { name, mainCategory, subCategory, unit, hsn, pendingQty, acceptedQty }
//     const materialMap = {};

//     allRequests.forEach(req => {
//         (req.items || []).forEach(item => {
//             const rm = item.rawMaterial || {};
//             const id = rm._id || rm.name || 'unknown';
//             if (!materialMap[id]) {
//                 materialMap[id] = {
//                     name: rm.name || '—',
//                     mainCat: rm.mainCategory?.name || '—',
//                     subCat: rm.subCategory?.name || '—',
//                     unit: rm.unit || '—',
//                     hsn: rm.hsn || '—',
//                     pendingQty: 0,
//                     acceptedQty: 0,
//                 };
//             }
//             const qty = Number(item.requestedQuantity || 0);
//             if (req.status === 'PENDING') materialMap[id].pendingQty += qty;
//             if (req.status === 'ACCEPTED') materialMap[id].acceptedQty += qty;
//         });
//     });

//     const rows = Object.values(materialMap).map((m, idx) => [
//     idx + 1,
//     m.name,
//     m.mainCat,
//     m.subCat,
//     m.unit,
//     m.pendingQty ? Number(m.pendingQty).toFixed(2) : '-',
//     m.acceptedQty ? Number(m.acceptedQty).toFixed(2) : '-',
//     (m.pendingQty + m.acceptedQty).toFixed(2),
// ]);

//     let totalPending = 0, totalAccepted = 0;
//     Object.values(materialMap).forEach(m => { totalPending += m.pendingQty; totalAccepted += m.acceptedQty; });

//     autoTable(doc, {
//         startY: 56,
//         head: [['#', 'Material', 'Main Cat', 'Sub Cat', 'Unit', 'Pending Qty', 'Accepted Qty', 'Total Qty']],
//         body: rows,
//         theme: 'grid',
//         headStyles: { fillColor: primaryColor, textColor: 255, fontStyle: 'bold', halign: 'center' },
//         styles: { fontSize: 8, cellPadding: 2 },
//         alternateRowStyles: { fillColor: [245, 250, 255] },
//         columnStyles: {
//             0: { halign: 'center', cellWidth: 10 },
//             2: { halign: 'left', cellWidth: 22 },
//             3: { halign: 'left', cellWidth: 22 },
//             4: { halign: 'center', cellWidth: 14 },
//             5: { halign: 'right', cellWidth: 22 },
//             6: { halign: 'right', cellWidth: 24 },
//             7: { halign: 'right', cellWidth: 20 },
//         },
//     });

//     // ── Totals ──
//     const finalY = doc.lastAutoTable.finalY + 8;
//     doc.setFontSize(9);
//     doc.setFont('helvetica', 'bold');
//     doc.text(
//         `Materials: ${rows.length}   |   Total Pending: ${totalPending.toFixed(2)}   |   Total Accepted: ${totalAccepted.toFixed(2)}   |   Grand Total: ${(totalPending + totalAccepted).toFixed(2)}`,
//         pageWidth - 14, finalY, { align: 'right' }
//     );

//     // ── Signature ──
//     doc.setFont('helvetica', 'normal');
//     doc.text('Authorized Signature', pageWidth - 60, finalY + 18);
//     doc.line(pageWidth - 60, finalY + 20, pageWidth - 14, finalY + 20);

//     // ── Footer ──
//     doc.setFontSize(7);
//     doc.setTextColor(100, 100, 100);
//     doc.text(`Generated on ${new Date().toLocaleString('en-IN')}`, 14, doc.internal.pageSize.getHeight() - 10);

//     doc.save(`Combined-Stock-Request-${new Date().toLocaleDateString('en-IN').replace(/\//g, '-')}.pdf`);
// };

const handleDownloadCombinedPDF = (allRequests) => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const primaryColor = [9, 165, 247];

    // ── Header background ──
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, 28, 'F');

    // Logo
    try { doc.addImage(IntendImages, 'PNG', 10, 4, 50, 20); } catch (e) { }

    // Header text
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('Cummulative Stock Request', (pageWidth / 2) + 20, 16, { align: 'center' });  // ← offset right to avoid logo overlap
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    // doc.text('Pending + Accepted Requisitions', pageWidth - 14, 16, { align: 'right' });

    doc.setTextColor(40, 40, 40);

    // ── Basic info ──
    // let startY = 34;
    // doc.setFontSize(9);
    // doc.setFont('helvetica', 'normal');
    // doc.text('Report Type:', 14, startY);
    // doc.setFont('helvetica', 'bold');
    // doc.text('Pending + Accepted', 44, startY);

    // doc.setFont('helvetica', 'normal');
    // doc.text('Date:', 110, startY);
    // doc.setFont('helvetica', 'bold');
    // doc.text(new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }), 130, startY);

    // // Count per status
    // const pendingReqs = allRequests.filter(r => r.status === 'PENDING');
    // const acceptedReqs = allRequests.filter(r => r.status === 'ACCEPTED');

    // doc.setFont('helvetica', 'normal');
    // doc.text('Pending Requests:', 14, startY + 6);
    // doc.setFont('helvetica', 'bold');
    // doc.text(String(pendingReqs.length), 52, startY + 6);

    // doc.setFont('helvetica', 'normal');
    // doc.text('Accepted Requests:', 14, startY + 12);
    // doc.setFont('helvetica', 'bold');
    // doc.text(String(acceptedReqs.length), 52, startY + 12);

    // ── Aggregate materials across PENDING + ACCEPTED ──
    // Key: rawMaterial._id → { name, mainCategory, subCategory, unit, hsn, totalQty }
    const materialMap = {};

    allRequests.forEach(req => {
        (req.items || []).forEach(item => {
            const rm = item.rawMaterial || {};
            const id = rm._id || rm.name || 'unknown';
            if (!materialMap[id]) {
                materialMap[id] = {
                    name: rm.name || '—',
                    mainCat: rm.mainCategory?.name || '—',
                    subCat: rm.subCategory?.name || '—',
                    unit: rm.unit || '—',
                    hsn: rm.hsn || '—',
                    totalQty: 0,
                };
            }
            const qty = Number(item.requestedQuantity || 0);
            materialMap[id].totalQty += qty;
        });
    });

    // Create rows with combined category column
    // With this:
    const rows = Object.values(materialMap)
        .sort((a, b) => {
            const catCompare = a.mainCat.localeCompare(b.mainCat);
            if (catCompare !== 0) return catCompare;
            return a.subCat.localeCompare(b.subCat); // secondary sort by subCat
        })
        .map((m, idx) => [
            idx + 1,
            m.name,
            `${m.mainCat} > ${m.subCat}`,
            Number(m.totalQty).toFixed(2),
            m.unit,
        ]);

    let totalQuantity = 0;
    Object.values(materialMap).forEach(m => { totalQuantity += m.totalQty; });

    autoTable(doc, {
        startY: 34,  // ← reduced from 56 since info section is commented out
        head: [['#', 'Material', 'Category', 'Req. Qty', 'Unit']],  // ← shorter header text
        body: rows,
        theme: 'grid',
        headStyles: {
            fillColor: primaryColor,
            textColor: 255,
            fontStyle: 'bold',
            halign: 'center'  // all headers centered
        },
        styles: { fontSize: 8, cellPadding: 2, font: 'helvetica' },
        alternateRowStyles: { fillColor: [245, 250, 255] },
        columnStyles: {
            0: { halign: 'center', cellWidth: 10 },
            1: { halign: 'left', cellWidth: 60 },
            2: { halign: 'left', cellWidth: 60 },  // ← slightly wider
            3: { halign: 'center', cellWidth: 25 },  // ← center-aligned, slightly wider
            4: { halign: 'center', cellWidth: 25 },  // ← center instead of right
        },
    });

    // ── Totals ──
    const finalY = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(
        `Materials: ${rows.length}`,
        pageWidth - 14, finalY, { align: 'right' }
    );

    // ── Signature ──
    doc.setFont('helvetica', 'normal');
    doc.text('Authorized Signature', pageWidth - 60, finalY + 18);
    doc.line(pageWidth - 60, finalY + 20, pageWidth - 14, finalY + 20);

    // ── Footer ──
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated on ${new Date().toLocaleString('en-IN')}`, 14, doc.internal.pageSize.getHeight() - 10);

    doc.save(`Cumulative-Stock-Request-${new Date().toLocaleDateString('en-IN').replace(/\//g, '-')}.pdf`);
};

/* ─── StatusPill ─────────────────────────────────────────── */
const StatusPill = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || { label: status, color: '#6b7280', bg: '#f3f4f6', border: '#d1d5db' };
    return (
        <span style={{
            padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
            letterSpacing: '0.04em', textTransform: 'uppercase',
            color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`,
            whiteSpace: 'nowrap',
        }}>
            {cfg.label}
        </span>
    );
};

/* ─── Items Expandable ───────────────────────────────────── */
const ItemsRow = ({ items, status }) => {
    const [open, setOpen] = useState(false);

    return (
        <div style={{ marginTop: 10 }}>
            <button onClick={() => setOpen(o => !o)} style={{
                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
                color: '#6366f1', fontSize: 13, fontWeight: 600,
            }}>
                <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 20, height: 20, borderRadius: 6,
                    background: '#eef2ff', color: '#6366f1', fontSize: 11, fontWeight: 800,
                }}>
                    {items.length}
                </span>
                Item{items.length !== 1 ? 's' : ''}
                <span style={{
                    fontSize: 9, display: 'inline-block',
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s',
                }}>▼</span>
            </button>

            {open && (
                <div style={{ marginTop: 8, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: status === 'RECEIVED' ? '2.5fr 1fr 1fr 1fr 140px 140px' : '2.5fr 1fr 1fr 1fr 140px',
                        background: '#f9fafb', padding: '7px 14px', borderBottom: '1px solid #e5e7eb',
                        fontSize: 11, fontWeight: 700, color: '#6b7280',
                        letterSpacing: '0.05em', textTransform: 'uppercase',
                    }}>
                        <span>Material</span><span>Category</span><span>HSN</span><span>Unit</span>
                        <span>Requested Qty</span>
                        {status === 'RECEIVED' && <span>Received Qty</span>}
                    </div>
                    {items.map((item, idx) => {
                        const rm = item.rawMaterial || {};
                        return (
                            <div key={item._id || idx} style={{
                                display: 'grid',
                                gridTemplateColumns: status === 'RECEIVED' ? '2.5fr 1fr 1fr 1fr 130px 130px' : '2.5fr 1fr 1fr 1fr 130px',
                                padding: '9px 14px', alignItems: 'center',
                                background: idx % 2 === 0 ? '#fff' : '#fafafa',
                                borderBottom: idx < items.length - 1 ? '1px solid #f3f4f6' : 'none',
                                fontSize: 13,
                            }}>
                                <div>
                                    <div style={{ fontWeight: 600, color: '#111827' }}>{rm.name || '—'}</div>
                                    {rm.description && (
                                        <div style={{
                                            color: '#9ca3af', fontSize: 11, marginTop: 1,
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300,
                                        }}>{rm.description}</div>
                                    )}
                                </div>
                                <span style={{ color: '#373b41', fontSize: 12 }}>{rm.subCategory?.name} <br />
                                    <div style={{ color: '#6b7280', fontSize: 12 }}>{rm.mainCategory?.name || '—'}</div>
                                </span>
                                <span style={{ color: '#6b7280', fontFamily: 'monospace', fontSize: 12 }}>{rm.hsn || '—'}</span>
                                <span style={{ color: '#6b7280', fontSize: 12 }}>{rm.unit || '—'}</span>
                                <span style={{ textAlign: 'left', fontWeight: 700, color: '#6366f1', fontSize: 14 }}>
                                    {item.requestedQuantity} <span style={{ fontSize: 12, color: '#6366f1', fontWeight: 500 }}>{rm.unit || ''}</span>
                                </span>
                                {status === 'RECEIVED' && (
                                    <span style={{ textAlign: 'left', fontWeight: 700, color: '#6366f1', fontSize: 14 }}>
                                        {item.receivedQuantity} <span style={{ fontSize: 12, color: '#6366f1', fontWeight: 500 }}>{rm.unit || ''}</span>
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

/* ─── Update Modal ───────────────────────────────────────── */
const UpdateModal = ({ request, onHide, onUpdated, accessToken }) => {
    const [status, setStatus] = useState('ACCEPTED');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Request is already finalised if it's ACCEPTED or REJECTED
    const alreadyUpdated = request.status === 'ACCEPTED' || request.status === 'REJECTED';

    const handleSave = async () => {
        setSaving(true); setError('');
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            // PUT method | body: { _id, status } | only ACCEPTED or REJECTED allowed
            await axiosInstance.put(`stock-requisition/update-status`, { _id: request._id, status }, { headers });
            onUpdated(request._id, status);
            onHide();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to update. Please try again.');

        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 1050,
            background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(3px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={onHide}>
            <div style={{
                background: '#fff', borderRadius: 14, width: '100%', maxWidth: 420,
                margin: '0 16px', boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
            }} onClick={e => e.stopPropagation()}>

                {/* header */}
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '16px 20px', borderBottom: '1px solid #f3f4f6',
                }}>
                    <span style={{ fontWeight: 800, fontSize: 16, color: '#111827' }}>Update Stock Request</span>
                    <button onClick={onHide} style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        fontSize: 22, color: '#9ca3af', lineHeight: 1, padding: 0,
                    }}>×</button>
                </div>

                {/* body */}
                <div style={{ padding: '20px' }}>

                    {/* indent + current status info */}
                    <div style={{
                        marginBottom: 16, padding: '10px 14px',
                        background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                        <div>
                            <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Indent Number
                            </div>
                            <div style={{ fontWeight: 700, color: '#6366f1', fontFamily: 'monospace', fontSize: 14 }}>
                                {request.indentNumber}
                            </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Current Status
                            </div>
                            <StatusPill status={request.status} />
                        </div>
                    </div>

                    {alreadyUpdated ? (
                        /* already finalised — cannot change */
                        <div style={{
                            padding: '16px', borderRadius: 9,
                            background: '#f9fafb', border: '1px solid #e5e7eb',
                            textAlign: 'center', color: '#6b7280', fontSize: 13, lineHeight: 1.6,
                        }}>
                            This request has already been{' '}
                            <strong style={{ color: '#374151' }}>{request.status.toLowerCase()}</strong>{' '}
                            and cannot be changed further.
                        </div>
                    ) : (
                        <>
                            <div style={{ marginBottom: 8, fontSize: 13, fontWeight: 700, color: '#374151' }}>
                                Set Status{' '}
                                <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 12 }}>(choose one)</span>
                            </div>
                            {/* side-by-side ACCEPTED / REJECTED radio cards */}
                            <div style={{ display: 'flex', gap: 10 }}>
                                {Object.entries(UPDATABLE_STATUSES).map(([key, cfg]) => (
                                    <label key={key} style={{
                                        flex: 1, display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', gap: 8,
                                        padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                                        border: `2px solid ${status === key ? cfg.border : '#e5e7eb'}`,
                                        background: status === key ? cfg.bg : '#fff',
                                        transition: 'all .15s', textAlign: 'center',
                                    }}>
                                        <input
                                            type="radio" name="status" value={key}
                                            checked={status === key}
                                            onChange={() => setStatus(key)}
                                            style={{ accentColor: cfg.color, width: 15, height: 15 }}
                                        />
                                        <span style={{ fontWeight: 700, fontSize: 13, color: cfg.color }}>
                                            {cfg.label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </>
                    )}

                    {error && (
                        <div style={{
                            marginTop: 12, padding: '10px 14px',
                            background: '#fee2e2', border: '1px solid #fca5a5',
                            borderRadius: 8, color: '#dc2626', fontSize: 13,
                        }}>{error}</div>
                    )}
                </div>

                {/* footer */}
                <div style={{
                    padding: '12px 20px', borderTop: '1px solid #f3f4f6',
                    display: 'flex', justifyContent: 'flex-end', gap: 8,
                }}>
                    <button onClick={onHide} style={{
                        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
                        padding: '8px 18px', fontSize: 13, fontWeight: 600,
                        color: '#374151', cursor: 'pointer', fontFamily: 'inherit',
                    }}>
                        {alreadyUpdated ? 'Close' : 'Cancel'}
                    </button>
                    {!alreadyUpdated && (
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            style={{
                                background: saving ? '#a5b4fc' : '#6366f1',
                                border: 'none', borderRadius: 8, padding: '8px 22px',
                                fontSize: 13, fontWeight: 700, color: '#fff',
                                cursor: saving ? 'not-allowed' : 'pointer',
                                display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'inherit',
                            }}>
                            {saving ? <><Spinner animation="border" size="sm" /> Saving…</> : 'Save Changes'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ─── Request Card ───────────────────────────────────────── */
const RequestCard = ({ req, onUpdate, onDownloadPDF, isDark }) => {
    const cardBg = isDark ? '#1a2132' : '#fff';
    const border = isDark ? '#2d3748' : '#e5e7eb';
    const text = isDark ? '#f3f4f6' : '#111827';
    const muted = isDark ? '#9ca3af' : '#6b7280';

    const isAccepted = req.status === 'ACCEPTED';

    return (
        <div style={{
            background: cardBg, border: `1px solid ${border}`, borderRadius: 10,
            padding: '16px 18px', marginBottom: 12,
            boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.05)',
            transition: 'box-shadow .2s',
        }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = isDark ? '0 4px 18px rgba(0,0,0,0.45)' : '0 4px 14px rgba(0,0,0,0.09)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.05)'}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 14, color: '#6366f1' }}>
                            {req.indentNumber}
                        </span>
                        <StatusPill status={req.status} />
                        <span style={{
                            fontSize: 11, color: muted,
                            background: isDark ? '#252d3d' : '#f3f4f6',
                            padding: '2px 8px', borderRadius: 6, fontWeight: 600,
                        }}>
                            FY {req.fin_year}
                        </span>
                    </div>
                    <div style={{ fontSize: 12, color: muted, marginTop: 4 }}>
                        Outlet:{' '}
                        <span style={{ color: text, fontWeight: 500 }}>
                            {req?.outlet?.name || req?.outlet?._id || '—'}, {req?.outlet?.city}
                        </span>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ textAlign: 'right', fontSize: 12, color: muted }}>
                        <div>Raised on: <span style={{ color: text }}>{fmt(req.createdAt)}</span></div>
                        {/* <div style={{ marginTop: 2 }}>Updated: <span style={{ color: text }}>{fmt(req.updatedAt)}</span></div> */}
                    </div>

                    {/* Update/Transfer Button */}
                    {req.status !== 'COMPLETED' && req.status !== 'RECEIVED' && (
                        <button onClick={() => onUpdate(req)} style={{
                            background: isAccepted ? '#d1fae5' : '#f5f3ff',
                            border: `1px solid ${isAccepted ? '#6ee7b7' : '#ddd6fe'}`,
                            borderRadius: 7, padding: '6px 13px',
                            fontSize: 12, fontWeight: 700,
                            color: isAccepted ? '#059669' : '#7c3aed',
                            cursor: 'pointer', whiteSpace: 'nowrap',
                            display: 'flex', alignItems: 'center', gap: 5,
                            fontFamily: 'inherit', transition: 'background .15s',
                        }}
                            onMouseEnter={e => e.currentTarget.style.background = isAccepted ? '#a7f3d0' : '#ede9fe'}
                            onMouseLeave={e => e.currentTarget.style.background = isAccepted ? '#d1fae5' : '#f5f3ff'}
                        >
                            {isAccepted ? '📦 Transfer Stock' : '✎ Update'}
                        </button>
                    )}

                    {/* Print/Download PDF Button */}
                    <button onClick={() => onDownloadPDF(req)} style={{
                        background: '#fef3c7',
                        border: '1px solid #fcd34d',
                        borderRadius: 7, padding: '6px 13px',
                        fontSize: 12, fontWeight: 700,
                        color: '#d97706',
                        cursor: 'pointer', whiteSpace: 'nowrap',
                        display: 'flex', alignItems: 'center', gap: 5,
                        fontFamily: 'inherit', transition: 'background .15s',
                    }}
                        onMouseEnter={e => e.currentTarget.style.background = '#fde68a'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fef3c7'}
                        title="Download PDF"
                    >
                        📄 PDF
                    </button>

                </div>

            </div>

            <ItemsRow items={req.items || []} status={req.status} />
        </div>
    );
};

/* ─── Main Component ─────────────────────────────────────── */
const StocksRequest = () => {

    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);
    const accessToken = localStorage.getItem('authToken');

    const [stockRequests, setStockRequests] = useState([]);
    const [selectedOutlet, setSelectedOutlet] = useState(null); // OutletSelect returns option object { value, label }
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    // const [statusFilter, setStatusFilter] = useState('ALL');
    const [statusFilter, setStatusFilter] = useState('PENDING');
    const [updateTarget, setUpdateTarget] = useState(null);

    const navigate = useNavigate();

    const isDark = skin === 'dark';
    const pageBg = isDark ? '#111827' : '#f3f4f6';
    const cardBg = isDark ? '#1a2132' : '#fff';
    const textMain = isDark ? '#f3f4f6' : '#111827';
    const textMuted = isDark ? '#9ca3af' : '#6b7280';
    const borderCol = isDark ? '#2d3748' : '#e5e7eb';
    const inputBg = isDark ? '#1e293b' : '#fff';

    /* ── fetch stock requests ── */
    const getStockRequests = async (outletOption) => {
        // outletOption: OutletSelect option object { value, label } | null | undefined
        const option = outletOption !== undefined ? outletOption : selectedOutlet;
        const outletId = option?.value || option?._id || '';
        setLoading(true); setError('');
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const params = outletId ? `?outlet=${outletId}` : '';
            const res = await axiosInstance.get(`stock-requisition${params}`, { headers });
            const raw = res?.data?.data?.data ?? [];
            console.log("Response of Stock Request", raw);
            const formattedData = Array.isArray(raw) ? raw : [raw].filter(Boolean);
            // ✅ Latest First (NEW DATA ON TOP)
            const sortedData = formattedData?.sort(
                (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
            );

            setStockRequests(sortedData);
        } catch (err) {
            console.error('Stock requests fetch error:', err);
            setError('Failed to load stock requests. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateClick = (req) => {
        if (req.status === 'ACCEPTED') {
            // Navigate to Stock Transfer screen and pass the request data
            navigate('/purchase/stock-transfer', { state: { request: req } });
        } else {
            // Open the local update modal for PENDING / FULFILLED / REJECTED
            setUpdateTarget(req);
        }
    };

    useEffect(() => { getStockRequests(''); }, []);

    const handleOutletChange = (option) => {
        setSelectedOutlet(option);
        getStockRequests(option);
    };

    /* optimistic UI update after modal save */
    const handleUpdated = (id, newStatus) => {
        setStockRequests(prev =>
            prev.map(r => r._id === id
                ? { ...r, status: newStatus, updatedAt: new Date().toISOString() }
                : r)
        );
    };

    const filtered = stockRequests.filter(req => {
        const matchSearch =
            !searchTerm ||
            req.indentNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            req.outlet?.outletName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            req.items?.some(i => i.rawMaterial?.name?.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchStatus = statusFilter === 'ALL' || req.status === statusFilter;
        return matchSearch && matchStatus;
    });

    const counts = stockRequests.reduce((a, r) => ({ ...a, [r.status]: (a[r.status] || 0) + 1 }), {});

    return (
        <React.Fragment>
            <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

            <Header onSkin={setSkin} />

            {updateTarget && (
                <UpdateModal
                    request={updateTarget}
                    onHide={() => setUpdateTarget(null)}
                    onUpdated={handleUpdated}
                    accessToken={accessToken}
                />
            )}

            <div className={`main main-app p-3 p-lg-3 ${skin === 'dark' ? 'bg-dark text-light' : 'bg-light'}`}
                style={{ minHeight: '100vh', background: pageBg, fontFamily: "'Nunito', 'Segoe UI', sans-serif" }}>
                <Container fluid>

                    {/* ── Page Header ── */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22, flexWrap: 'wrap', gap: 12 }}>
                        <div>
                            <h4 style={{ fontWeight: 800, fontSize: 21, margin: 0, color: textMain, letterSpacing: '-0.01em' }}>
                                Stock Requests
                            </h4>
                            <p style={{ color: textMuted, margin: '3px 0 0', fontSize: 13 }}>
                                Manage and track material requisitions from outlets
                            </p>
                        </div>
                        <button onClick={() => getStockRequests()} disabled={loading} style={{
                            background: '#6366f1', color: '#fff', border: 'none', borderRadius: 9,
                            padding: '9px 18px', fontWeight: 700, fontSize: 13,
                            cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
                            display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'inherit',
                        }}>
                            {loading
                                ? <><Spinner animation="border" size="sm" /> Refreshing…</>
                                : <><span style={{ fontSize: 15 }}>↺</span> Refresh</>}
                        </button>
                    </div>

                    {/* ── Outlet Filter + Search ── */}
                    <div style={{
                        background: cardBg, border: `1px solid ${borderCol}`,
                        borderRadius: 10, padding: '12px 16px', marginBottom: 14,
                        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                    }}>
                        {/* OutletSelect */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ minWidth: 300 }}>
                                <Form.Group controlId="outletSelect" style={{ margin: 0 }}>
                                    <OutletSelect
                                        value={selectedOutlet}
                                        onChange={handleOutletChange}
                                        required={false}
                                    />
                                </Form.Group>
                            </div>
                        </div>

                        <div style={{ width: 1, height: 50, background: borderCol }} />

                        {/* Search */}
                        <div style={{ minWidth: 300, flex: 1 }}>
                            <Form.Label className="fw-semibold" style={{ color: textMuted, marginBottom: 4 }}>
                                Search
                            </Form.Label>
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                background: isDark ? '#252d3d' : '#f9fafb',
                                border: `1px solid ${borderCol}`, borderRadius: 8, padding: '6px 12px',
                            }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={textMuted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>
                                <input
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    placeholder="Search by indent no., outlet, or material…"
                                    style={{
                                        border: 'none', background: 'transparent', flex: 1,
                                        outline: 'none', color: textMain, fontSize: 13, fontFamily: 'inherit',
                                    }}
                                />
                                {searchTerm && (
                                    <button onClick={() => setSearchTerm('')} style={{
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        color: textMuted, fontSize: 18, lineHeight: 1, padding: 0,
                                    }}>×</button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Status Filter Chips ── */}
                    {!loading && stockRequests.length > 0 && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
                            {[
                                { key: 'ALL', label: 'All' },
                                ...Object.keys(STATUS_CONFIG).map(k => ({ key: k, label: STATUS_CONFIG[k].label }))
                            ].map(({ key, label }) => {
                                const count = key === 'ALL' ? stockRequests.length : (counts[key] || 0);
                                const cfg = STATUS_CONFIG[key] || {};
                                const active = statusFilter === key;
                                return (
                                    <button key={key} onClick={() => setStatusFilter(key)} style={{
                                        border: `1.5px solid ${active ? (cfg.border || '#a5b4fc') : borderCol}`,
                                        background: active ? (cfg.bg || '#eef2ff') : cardBg,
                                        color: active ? (cfg.color || '#6366f1') : textMuted,
                                        borderRadius: 20, padding: '4px 13px', fontSize: 12,
                                        fontWeight: 700, cursor: 'pointer', transition: 'all .15s',
                                        fontFamily: 'inherit',
                                    }}>
                                        {label} ({count})
                                    </button>
                                );
                            })}
                            {/* Combined PDF button — visible only on PENDING or ACCEPTED tab */}
                            {(statusFilter === 'PENDING' || statusFilter === 'ACCEPTED') && (() => {
                                const combined = stockRequests.filter(r => r.status === 'PENDING' || r.status === 'ACCEPTED');
                                return combined.length > 0 ? (
                                    <button
                                        onClick={() => handleDownloadCombinedPDF(combined)}
                                        style={{
                                            marginLeft: 'auto',
                                            background: '#fef3c7',
                                            border: '1.5px solid #fcd34d',
                                            borderRadius: 20,
                                            padding: '4px 14px',
                                            fontSize: 12,
                                            fontWeight: 700,
                                            color: '#d97706',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 5,
                                            fontFamily: 'inherit',
                                            whiteSpace: 'nowrap',
                                            transition: 'background .15s',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#fde68a'}
                                        onMouseLeave={e => e.currentTarget.style.background = '#fef3c7'}
                                        title="Download combined PDF of all Pending + Accepted requests"
                                    >
                                        📄 Cumulative PDF
                                    </button>
                                ) : null;
                            })()}
                        </div>
                    )}

                    {/* ── Error ── */}
                    {error && (
                        <div style={{
                            background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 9,
                            padding: '11px 15px', marginBottom: 14, color: '#dc2626', fontSize: 13,
                        }}>{error}</div>
                    )}

                    {/* ── Loading Skeleton ── */}
                    {loading && (
                        <>
                            <style>{`@keyframes shimmer{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
                            {[1, 2, 3].map(i => (
                                <div key={i} style={{
                                    background: cardBg, border: `1px solid ${borderCol}`, borderRadius: 10,
                                    padding: '16px 18px', marginBottom: 12,
                                    animation: 'shimmer 1.5s ease-in-out infinite',
                                }}>
                                    <div style={{ height: 14, width: '32%', background: isDark ? '#2d3748' : '#e5e7eb', borderRadius: 5, marginBottom: 10 }} />
                                    <div style={{ height: 11, width: '20%', background: isDark ? '#2d3748' : '#e5e7eb', borderRadius: 5 }} />
                                </div>
                            ))}
                        </>
                    )}

                    {/* ── Empty State ── */}
                    {!loading && !error && filtered.length === 0 && (
                        <div style={{
                            background: cardBg, border: `1px solid ${borderCol}`, borderRadius: 10,
                            padding: '52px 20px', textAlign: 'center',
                        }}>
                            <div style={{ fontSize: 36, marginBottom: 10 }}>📦</div>
                            <div style={{ fontWeight: 800, fontSize: 15, color: textMain, marginBottom: 5 }}>
                                {searchTerm || statusFilter !== 'ALL' ? 'No matching requests' : 'No stock requests found'}
                            </div>
                            <div style={{ color: textMuted, fontSize: 13 }}>
                                {searchTerm || statusFilter !== 'ALL'
                                    ? 'Try adjusting your search or filters'
                                    : selectedOutlet?.value
                                        ? 'This outlet has no stock requests yet'
                                        : 'Stock requisitions will appear here once raised'}
                            </div>
                        </div>
                    )}

                    {/* ── Request Cards ── */}
                    {!loading && filtered.map(req => (
                        <RequestCard
                            key={req._id}
                            req={req}
                            isDark={isDark}
                            onUpdate={(r) => handleUpdateClick(r)}
                            onDownloadPDF={handleDownloadPDF}
                        />
                    ))}

                    {/* ── Footer Count ── */}
                    {!loading && filtered.length > 0 && (
                        <div style={{ textAlign: 'center', color: textMuted, fontSize: 12, marginTop: 6 }}>
                            Showing {filtered.length} of {stockRequests.length} request{stockRequests.length !== 1 ? 's' : ''}
                        </div>
                    )}

                </Container>
            </div>
        </React.Fragment>
    );
};

export default StocksRequest;